import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import { setCompanyEmailImportEnabled } from '../../../../api/firebase/function/emailImportFunction';
import {
  gray40,
  primaryButtonBackgroundColor,
  red20,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../../constants/colors';
import { dialogText, dialogTitle } from '../../../../constants/location';
import CustomTextfield from '../../../uiParts/form/CustomTextfield';

interface Props {
  open: boolean;
  companyId: string;
  /** 変更前の有効状態。ダイアログはこの反対の状態へ切り替える前提で開く。 */
  currentEnabled: boolean;
  onClose: () => void;
  /** 切替成功時に新しい有効状態を返す。 */
  onChanged: (after: boolean) => void;
}

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  bodyText: {
    ...dialogText,
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    padding: '8px 12px',
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
  actionButton: {
    height: '44px',
    minWidth: '120px',
  },
};

const EmailImportFeatureToggleDialog: React.FC<Props> = ({
  open,
  companyId,
  currentEnabled,
  onClose,
  onChanged,
}) => {
  const pendingState = !currentEnabled;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setError('');
      setNote('');
    }
  }, [open]);

  const handleClose = (): void => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    setSubmitting(true);
    setError('');
    try {
      await setCompanyEmailImportEnabled({
        company_id: companyId,
        enabled: pendingState,
        note: note || (pendingState ? '有効化' : '無効化'),
      });
      onChanged(pendingState);
      onClose();
    } catch (e) {
      Sentry.captureException(e);
      setError(e instanceof Error ? e.message : '切替に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>
          メール取り込み機能を{pendingState ? '有効化' : '無効化'}しますか？
        </Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={styles.fieldWrapper}>
        {error && <Box sx={styles.errorBox}>{error}</Box>}

        {pendingState ? (
          <>
            <Typography sx={styles.bodyText}>
              有効化すると、この企業の専用受信アドレス宛メールの添付が自動で OCR
              に取り込まれます。プラン種別に関わらず、有効化した企業がすぐに利用できます。
            </Typography>
            <CustomTextfield
              labelText="備考 (任意)"
              value={note}
              onChange={(e): void => setNote(e.target.value)}
              multiline
              minRows={2}
              inputWidth="100%"
              fontSizeIs14px
              formMb="0"
            />
          </>
        ) : (
          <>
            <Typography sx={styles.bodyText}>
              無効化すると、この企業宛に届いたメールは取り込まれず、受信ログに rejected
              として記録されます。顧客への通知漏れに注意してください。
            </Typography>
            <CustomTextfield
              labelText="備考 (任意)"
              value={note}
              onChange={(e): void => setNote(e.target.value)}
              multiline
              minRows={2}
              inputWidth="100%"
              fontSizeIs14px
              formMb="0"
            />
          </>
        )}

        <Box sx={styles.dialogActions}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={[primaryButtonBackgroundColor, styles.actionButton]}
          >
            {pendingState ? '有効化する' : '無効化する'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EmailImportFeatureToggleDialog;
